/**
 * Mehndi coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mehndiColoringPagesPack: ToilePack = {
    slug: 'mehndi-coloring-pages',
    category: 'coloring',
    name: 'Mehndi coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hand', label: 'Mehndi hand', subject: 'an open hand outline with a mandala in the palm, vines and dots running up each finger and lace bands at the wrist'},
        {key: 'peacock', label: 'Peacock motif', subject: 'a mehndi peacock with a curled neck, a tail of paisley feathers, lotus buds and fine dotted borders'},
        {key: 'mandala', label: 'Henna mandala', subject: 'a full-page henna mandala of layered petals, paisleys, checkered bands and a lotus at its center'},
    ],
    styles: [
        {key: 'bridal', label: 'Bridal', style: 'fine-line Indian bridal mehndi, thin single-weight lines, dense paisleys, petals, checkered bands and tiny dots with little empty space', palette: 'black ink only', framing: 'the design centered with a white margin'},
        {key: 'arabic', label: 'Arabic', style: 'Arabic mehndi, bold flowing lines, large open florals and leaves trailing on a diagonal with generous open space', palette: 'black ink only', framing: 'the design centered with a white margin'},
        {key: 'beginner', label: 'Beginner', style: 'a beginner mehndi style, thick bold outlines, a few large petals, simple paisleys and big dots easy to color', palette: 'black ink only', framing: 'the design large and centered with a wide white margin'},
    ],
    directions: ['feet design', 'Eid moon', 'a lotus'],
    directionHint: 'try: feet design',
    seo: {
        title: 'Mehndi coloring pages: hand, peacock, henna mandala',
        metaDescription: 'Nine mehndi coloring pages: a hand with henna patterns, a peacock motif and a full-page mandala, in bridal, Arabic and beginner styles.',
        h1: 'Mehndi coloring pages: mehndi hand, peacock motif, henna mandala',
        tagline: 'Henna patterns in bridal, Arabic and beginner styles, from a single hand to a full page.',
        intro: 'Mehndi coloring pages for anyone who loves henna patterns, from girls practising designs before a wedding or Eid to adults who color intricate work. The nine pages show an open hand, a peacock motif and a full-page henna mandala. The styles are bridal, Arabic and beginner. Type a different motif, and the same style carries it.',
        useCases: [
            'practice designs before a wedding or Eid',
            'a detailed page for an adult colorer',
            'a henna-themed party activity',
            'a first design for a beginner to fill',
        ],
        faq: [
            {q: 'What is the difference between the styles?', a: 'Bridal is fine-line work with thin single-weight lines, dense paisleys and tiny dots. Arabic uses bold flowing lines and large open florals, and beginner uses thick outlines with a few large petals and big dots.'},
            {q: 'Which style suits a beginner?', a: 'The beginner style, with thick outlines and large simple shapes, is the easiest to fill.'},
            {q: 'Can I ask for a different motif?', a: 'Yes. A motif you type is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color with henna-style tones.'},
        ],
    },
}
